import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { getAgentCapabilities, type AgentCapabilities } from '../configs/agentCapabilities';
import { useAuth } from '../context/AuthContextType';
import apiClient from '../services/api';

interface BackendTool {
  name: string;
  description: string;
}

interface AgentInfoPanelProps {
  activeField?: string | null;
  entityType?: string;
  canEdit?: boolean;
  kbId?: string;
  kbName?: string;
  solutionId?: string;
  fieldLabels?: Record<string, { label: string; description: string }>;
  showMetrics?: boolean;
  capabilities?: AgentCapabilities;
  activeFieldLabel?: string;
  agentLabelOverride?: string;
}

const S = {
  sectionLabel: { fontWeight: 600, color: '#495057', marginBottom: 3 } as React.CSSProperties,
  pill: { background: '#e7f5ff', color: '#1971c2', padding: '2px 8px', borderRadius: 10, fontWeight: 600 } as React.CSSProperties,
  fileRow: {
    display: 'flex', alignItems: 'center', gap: 6,
    background: '#f8f9fa', borderRadius: 4, padding: '3px 7px',
    border: '1px solid #e9ecef', marginBottom: 3,
  } as React.CSSProperties,
  fileName: { fontFamily: 'monospace', fontSize: '0.76rem', color: '#343a40', flex: 1 } as React.CSSProperties,
  badge: { color: '#868e96', fontSize: '0.7rem', whiteSpace: 'nowrap' as const },
  viewBtn: {
    padding: '1px 7px', borderRadius: 3, border: '1px solid #ced4da',
    background: 'white', color: '#1971c2', fontSize: '0.7rem',
    cursor: 'pointer', whiteSpace: 'nowrap' as const, flexShrink: 0,
  } as React.CSSProperties,
  pre: {
    marginTop: 6, padding: '7px 10px',
    background: '#f1f3f5', border: '1px solid #dee2e6', borderRadius: 4,
    fontSize: '0.71rem', lineHeight: 1.5, whiteSpace: 'pre-wrap' as const,
    wordBreak: 'break-word' as const, maxHeight: 340, overflowY: 'auto' as const,
    color: '#212529', fontFamily: 'monospace',
  } as React.CSSProperties,
  sharedBadge: {
    fontSize: '0.65rem', color: '#868e96', background: '#f1f3f5',
    border: '1px solid #e9ecef', borderRadius: 3, padding: '0 4px',
  } as React.CSSProperties,
};

const AgentInfoPanel: React.FC<AgentInfoPanelProps> = ({
  activeField = null,
  entityType = '',
  canEdit = false,
  kbId,
  kbName,
  solutionId,
  fieldLabels = {},
  showMetrics = true,
  capabilities,
  activeFieldLabel,
  agentLabelOverride,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);

  // Single unified viewer: which file is currently being shown
  const [viewingFile, setViewingFile] = useState<string | null>(null);
  const [viewContent, setViewContent] = useState<string>('');
  const [viewLoading, setViewLoading] = useState(false);
  const [viewError, setViewError] = useState(false);

  // Backend-fetched real tool list (CFA only; Pilot uses pre-computed capabilities prop)
  const isPrecomputed = !!capabilities;
  const [backendTools, setBackendTools] = useState<BackendTool[] | null>(null);
  const [toolsLoading, setToolsLoading] = useState(false);

  useEffect(() => {
    if (!open || isPrecomputed) return;
    setToolsLoading(true);
    setBackendTools(null);
    const params = new URLSearchParams({ entity_type: entityType, can_edit: String(canEdit) });
    if (activeField) params.set('field', activeField);
    if (kbId) params.set('kb_id', kbId);
    if (solutionId) params.set('solution_id', solutionId);
    apiClient
      .get<{ tools: BackendTool[] }>(`/api/agent/tools?${params}`)
      .then(res => setBackendTools(res.data.tools))
      .catch(() => setBackendTools(null))
      .finally(() => setToolsLoading(false));
  }, [open, isPrecomputed, activeField, entityType, canEdit, kbId, solutionId]);

  const caps = capabilities ?? getAgentCapabilities(activeField, entityType, canEdit, kbId, kbName);
  const agentLabel = agentLabelOverride ?? t(`scv2.workflow.agents.${caps.agentName}`, caps.agentName);
  const resolvedFieldLabel = activeFieldLabel ?? (activeField ? (fieldLabels[activeField]?.label ?? activeField) : '—');
  const isSysAdmin = user?.role === 'SysAdmin';

  const toggleView = async (file: string) => {
    if (viewingFile === file) { setViewingFile(null); return; }
    setViewingFile(file);
    if (viewContent && viewingFile === file) return; // already loaded
    setViewLoading(true);
    setViewError(false);
    setViewContent('');
    try {
      const res = await apiClient.get<{ content: string }>(`/admin/prompts/${file}`);
      setViewContent(res.data.content);
    } catch {
      setViewError(true);
    } finally {
      setViewLoading(false);
    }
  };

  if (!showMetrics) return null;

  const renderFileRow = (file: string, shared?: boolean) => (
    <div key={file} style={{ marginBottom: 2 }}>
      <div style={S.fileRow}>
        <span style={S.fileName}>{file}</span>
        {shared && <span style={S.sharedBadge}>{t('scv2.cfa.sharedPrompt')}</span>}
        <span style={S.badge}>backend/prompts/</span>
        {isSysAdmin && (
          <button style={S.viewBtn} onClick={() => toggleView(file)} disabled={viewLoading && viewingFile === file}>
            {viewLoading && viewingFile === file
              ? t('scv2.cfa.promptLoading')
              : viewingFile === file
                ? t('scv2.cfa.hidePrompt')
                : t('scv2.cfa.viewPrompt')}
          </button>
        )}
      </div>
      {viewingFile === file && !viewLoading && (
        viewError
          ? <div style={{ color: '#e03131', fontSize: '0.75rem', marginBottom: 4 }}>{t('scv2.cfa.promptError')}</div>
          : <pre style={S.pre}>{viewContent}</pre>
      )}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setViewingFile(null); }}>
      <DialogTrigger asChild>
        <button
          title={t('scv2.cfa.infoButton')}
          style={{
            padding: '2px 7px', borderRadius: 4, border: '1px solid #ced4da',
            background: 'white', color: '#495057', fontSize: '0.72rem',
            fontWeight: 600, cursor: 'pointer', lineHeight: '18px',
            flexShrink: 0, whiteSpace: 'nowrap',
          }}
        >
          {t('scv2.cfa.buttonLabel')}
        </button>
      </DialogTrigger>

      <DialogContent style={{ maxWidth: 520, fontSize: '0.82rem' }}>
        <DialogHeader>
          <DialogTitle style={{ fontSize: '0.95rem' }}>{t('scv2.cfa.infoTitle')}</DialogTitle>
        </DialogHeader>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 13 }}>

          {/* Agent + field */}
          <div>
            <div style={S.sectionLabel}>{t('scv2.cfa.agentLabel')}</div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={S.pill}>{agentLabel}</span>
              <span style={{ color: '#868e96' }}>
                {t('scv2.cfa.activeFieldLabel')}: <strong>{resolvedFieldLabel}</strong>
              </span>
            </div>
          </div>

          {/* Tools */}
          <div>
            <div style={S.sectionLabel}>{t('scv2.cfa.toolsLabel')}</div>
            {isPrecomputed ? (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  {caps.tools.map(tool => (
                    <tr key={tool.name} style={{ borderBottom: '1px solid #f1f3f5' }}>
                      <td style={{ padding: '3px 0', width: 14 }}>
                        <span style={{
                          display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
                          background: tool.available ? '#51cf66' : '#ced4da', flexShrink: 0,
                        }} />
                      </td>
                      <td style={{ padding: '3px 6px', fontFamily: 'monospace', color: '#343a40', fontSize: '0.78rem' }}>
                        {tool.name}
                      </td>
                      <td style={{ padding: '3px 0', color: '#868e96' }}>
                        {tool.available
                          ? t(tool.descriptionKey)
                          : <em>{tool.unavailableReasonKey ? t(tool.unavailableReasonKey) : ''}</em>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : toolsLoading ? (
              <span style={{ color: '#adb5bd', fontSize: '0.78rem' }}>{t('scv2.cfa.toolsLoading', 'Loading…')}</span>
            ) : backendTools ? (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  {backendTools.map(tool => (
                    <tr key={tool.name} style={{ borderBottom: '1px solid #f1f3f5' }}>
                      <td style={{ padding: '3px 0', width: 14 }}>
                        <span style={{
                          display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
                          background: '#51cf66', flexShrink: 0,
                        }} />
                      </td>
                      <td style={{ padding: '3px 6px', fontFamily: 'monospace', color: '#343a40', fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                        {tool.name}
                      </td>
                      <td style={{ padding: '3px 0', color: '#868e96', maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                          title={tool.description}>
                        {tool.description.split('\n')[0]}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <span style={{ color: '#ced4da', fontSize: '0.78rem' }}>—</span>
            )}
          </div>

          {/* Fields */}
          <div>
            <div style={S.sectionLabel}>{t('scv2.cfa.fieldsLabel')}</div>
            <div style={{ display: 'flex', gap: 16 }}>
              <div>
                <div style={{ color: '#868e96', fontSize: '0.75rem', marginBottom: 2 }}>{t('scv2.cfa.writeLabel')}</div>
                {caps.writableFields.length > 0
                  ? <ul style={{ margin: 0, paddingLeft: 16 }}>
                      {caps.writableFields.map(f => (
                        <li key={f} style={{ color: '#2f9e44' }}>{fieldLabels[f]?.label ?? activeFieldLabel ?? f}</li>
                      ))}
                    </ul>
                  : <span style={{ color: '#ced4da' }}>{t('scv2.cfa.noneLabel')}</span>}
              </div>
              <div>
                <div style={{ color: '#868e96', fontSize: '0.75rem', marginBottom: 2 }}>{t('scv2.cfa.readLabel')}</div>
                <span style={{ color: '#495057' }}>{t('scv2.cfa.allFields')}</span>
              </div>
            </div>
          </div>

          {/* KBs */}
          <div>
            <div style={S.sectionLabel}>{t('scv2.cfa.kbLabel')}</div>
            {caps.kbs.length === 0
              ? <span style={{ color: '#ced4da' }}>{t('scv2.cfa.noKb')}</span>
              : <ul style={{ margin: 0, paddingLeft: 16 }}>
                  {caps.kbs.map(kb => (
                    <li key={kb.id} style={{ color: '#495057' }}>
                      {kb.name}
                      <span style={{ color: '#868e96', marginLeft: 6, fontSize: '0.75rem' }}>
                        ({t(`scv2.cfa.kbType.${kb.type}`)})
                      </span>
                    </li>
                  ))}
                </ul>}
          </div>

          {/* System prompt */}
          {caps.systemPromptFile && (
            <div>
              <div style={S.sectionLabel}>{t('scv2.cfa.systemPromptLabel')}</div>
              {renderFileRow(caps.systemPromptFile)}
            </div>
          )}

          {/* Task prompts */}
          {caps.taskPrompts.length > 0 && (
            <div>
              <div style={S.sectionLabel}>{t('scv2.cfa.taskPromptsLabel')}</div>
              {caps.taskPrompts.map(p => renderFileRow(p.file, p.shared))}
            </div>
          )}

          {/* Footer */}
          <div style={{ borderTop: '1px solid #f1f3f5', paddingTop: 9, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Link
              to="/help?doc=CFA_USER_GUIDE.md"
              style={{ color: '#1971c2', fontSize: '0.78rem', textDecoration: 'none' }}
              onClick={() => setOpen(false)}
            >
              {t('scv2.cfa.viewDocs')} &rarr;
            </Link>
            {isSysAdmin && (
              <Link
                to={`/admin/prompts#${caps.agentName}`}
                style={{ color: '#868e96', fontSize: '0.72rem', textDecoration: 'none' }}
                onClick={() => setOpen(false)}
              >
                {t('scv2.cfa.manageAllPrompts')} &rarr;
              </Link>
            )}
          </div>

        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AgentInfoPanel;
